// Client-side helpers shared by the redesigned report pages (mockup reports/*): sorting, paging
// and CSV download over rows the page has already loaded. The reports' APIs are unchanged.

export const REPORT_PAGE_SIZES = [10, 25, 50, 100];

/**
 * Date Range presets: the mockup's (Custom, This Week, This Month, Last 30 Days) plus the ones the
 * reports already offered. Values are CommonService.dateRangeSubmit codes ('4' is month to date).
 */
export const REPORT_DATE_RANGES = [
  { value: '0', label: 'Custom' },
  { value: 'W', label: 'This Week' },
  { value: '4', label: 'This Month' },
  { value: '5', label: 'Last 7 Days' },
  { value: '1', label: 'Last 30 Days' },
  { value: '2', label: 'Last 90 Days' },
  { value: '3', label: 'Year to Date' },
];

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

/** Sorts and pages one list of rows. `rows` is replaced wholesale whenever a report loads. */
export class ReportTable<T> {
  rows: T[] = [];
  sortKey = '';
  sortDir: 'asc' | 'desc' = 'asc';
  page = 1;
  pageSize = 10;

  constructor(private valueOf: (row: T, key: string) => any = (row, key) => row[key]) { }

  set(rows: T[]) {
    this.rows = Array.isArray(rows) ? rows.slice() : [];
    this.page = 1;
    this.applySort();
  }

  sortBy(key: string, firstDir: 'asc' | 'desc' = 'asc') {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = firstDir;
    }
    this.page = 1;
    this.applySort();
  }

  sortState(key: string): string {
    return this.sortKey !== key ? 'none' : (this.sortDir === 'asc' ? 'ascending' : 'descending');
  }

  private applySort() {
    if (!this.sortKey) {
      return;
    }
    const sign = this.sortDir === 'asc' ? 1 : -1;
    this.rows.sort((x, y) => {
      const a = this.valueOf(x, this.sortKey), b = this.valueOf(y, this.sortKey);
      const na = toNumber(a), nb = toNumber(b);
      if (na !== null && nb !== null) {
        return sign * (na - nb);
      }
      return sign * collator.compare(a == null ? '' : String(a), b == null ? '' : String(b));
    });
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 1;
  }

  get pages(): number {
    return Math.max(1, Math.ceil(this.rows.length / this.pageSize));
  }

  get pageRows(): T[] {
    const start = (this.page - 1) * this.pageSize;
    return this.rows.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return pageNumbers(this.page, this.pages);
  }

  goTo(page: number) {
    if (page >= 1 && page <= this.pages) {
      this.page = page;
    }
  }

  rangeLabel(noun: string, nouns: string): string {
    const total = this.rows.length;
    if (!total) {
      return 'No ' + nouns;
    }
    const from = (this.page - 1) * this.pageSize + 1;
    const to = Math.min(total, this.page * this.pageSize);
    return 'Showing ' + from + '–' + to + ' of ' + total + ' ' + (total === 1 ? noun : nouns);
  }
}

/** Up to five page numbers around the current page. */
export function pageNumbers(page: number, pages: number): number[] {
  const from = Math.max(1, Math.min(page - 2, pages - 4));
  const to = Math.min(pages, from + 4);
  const list: number[] = [];
  for (let p = from; p <= to; p++) {
    list.push(p);
  }
  return list;
}

/** Datepicker value { year, month, day } -> "mm/dd/yyyy" ('' when empty). */
export function dateLabel(d: { year: number, month: number, day: number }): string {
  return d && d.year ? `${('0' + d.month).slice(-2)}/${('0' + d.day).slice(-2)}/${d.year}` : '';
}

/** "mm/dd/yyyy" or "mm-dd-yyyy" -> "yyyymmdd" so dates sort in order; anything else ('-', '') -> ''. */
export function sortableDate(value: string): string {
  const m = /^(\d\d)[\/-](\d\d)[\/-](\d{4})$/.exec(value || '');
  return m ? m[3] + m[1] + m[2] : '';
}

/** "1,234.50", "$1,234.50", "12.5%" -> number; anything else -> null. */
export function toNumber(value: any): number {
  if (typeof value === 'number') {
    return isFinite(value) ? value : null;
  }
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).replace(/[$,%\s]/g, '');
  return text !== '' && /^-?\d*\.?\d+$/.test(text) ? parseFloat(text) : null;
}

/** Downloads rows (first row = header) as a UTF-8 CSV that opens cleanly in Excel. */
export function downloadCsv(filename: string, rows: any[][]) {
  const cell = (value: any) => {
    let s = value === null || value === undefined ? '' : String(value).trim();
    if (/^[=+\-@]/.test(s) && toNumber(s) === null) {
      s = "'" + s;   // keep spreadsheets from running a cell as a formula
    }
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = rows.map(r => r.map(cell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** True when text (case-insensitive) appears in any of the values. */
export function matches(search: string, values: any[]): boolean {
  const q = (search || '').trim().toLowerCase();
  return !q || values.some(v => v !== null && v !== undefined && String(v).toLowerCase().indexOf(q) !== -1);
}
